<template>
  <div class="parking-fee-container">
    <div class="nav-bar">
      <div class="title">扫码停车</div>
    </div>
    <div class="main" v-if="showParkingInfo && parkingInfo.licensePlateNumber">
      <div class="header">
        <van-divider :hairline="false" :style="{
          color: '#333',
          margin: 0,
          fontSize: 14,
          fontWeight: 700
        }">车牌号</van-divider>
        <div class="car-no">{{ parkingInfo.licensePlateNumber }}</div>
      </div>

      <div class="content">
        <div class="title">停车信息</div>
        <div class="list">
          <div class="item">
            <div class="leading">停车场名称</div>
            <div class="trailing">{{ parkingInfo.parkName || '-' }}</div>
          </div>
          <div class="item">
            <div class="leading">已停时间</div>
            <div class="trailing">{{ parkingInfo.parkTime || '-' }}</div>
          </div>
          <div class="item">
            <div class="leading">进场时间</div>
            <div class="trailing">{{ parkingInfo.parkStartTime || '-' }}</div>
          </div>
          <div class="item">
            <div class="leading">停车费用</div>
            <div class="trailing primary">{{ parkingInfo.payAmount }}元</div>
          </div>
          <div class="item">
            <div class="leading">充电时长</div>
            <div class="trailing primary">{{ formatChargeTime(parkingInfo.chargeTime) }}</div>
          </div>
        </div>
      </div>

      <div class="footer">
        <div class="pay-btn" :class="{ disabled: paying }" @click="handlePay">
          {{ paying ? '正在发起支付…' : `立即缴费（${parkingInfo.payAmount}元）` }}
        </div>

        <div class="explain">
          <div class="title">临停支付说明</div>

          <div class="desc-list">
            <div class="p">
              1.若因特殊原因无法查询车辆及缴费信息，请至人工收费处或联系客服进行处理
              <span class="strong">客服热线: <a href="tel:0871-68888889">0871-68888889</a></span>。
            </div>
            <div class="p">2.缴费前请仔细核对车场及车牌号信息。</div>
            <div class="p">
              3.玉溪站00:00-8:00免费放行，8:00以后出场按照入场时间全程收费。
            </div>
            <div class="p">
              4.若支付超时或者支付错误，请重新扫码支付。
            </div>
            <div class="p">
              5.若无法加载页面，请检查网络是否畅通，或重新加载页面。
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="other-status" v-else-if="!loading">
      <template v-if="isSuccess">
        <img class="icon" src="@/assets/images/icons/icon-success.png" alt="success" />
        <div class="status">支付成功, 感谢您的使用~</div>
        <div class="hint">为避免产生额外费用，支付成功后请立即出场</div>
      </template>
      <template v-else>
        <img class="no-data" src="@/assets/images/icons/no-data.png" alt="no-data" />
        <div class="no-vehicle">暂未查询到车辆信息</div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import {
  getNoPlateWaitPayOrder,
  getIsWechatPayPark,
  getParkingInfo,
  parkingPay
} from '@/api/modules/parking'
import { getLoginCode } from '@/utils/authorize'
import {
  buildParkingPayPayload,
  normalizeWechatPayData
} from './payment-flow'
export default {
  created() {
    this.initializeParkingInfo()
  },
  data() {
    return {
      isSuccess: false,
      parkingInfo: {},
      loading: false,
      paying: false,
      loginCode: '',
      isWechatPayPark: false
    }
  },
  computed: {
    isAli() {
      return /AlipayClient/.test(window.navigator.userAgent)
    },
    showParkingInfo() {
      if (!this.isSuccess && !this.loading) {
        return true
      }
      return false
    }
  },
  methods: {
    async initializeParkingInfo() {
      const { parkId, laneCode, plateNo, noPlate } = this.$route.query
      this.loginCode = getLoginCode(parkId) || ''

      try {
        this.isWechatPayPark = await getIsWechatPayPark({ parkId })
      } catch (error) {
        return
      }

      if (noPlate) {
        if (!this.loginCode) {
          return
        }
        await this.getParkingInfo({
          plateNo,
          parkId,
          laneCode,
          loginCode: this.loginCode
        }, getNoPlateWaitPayOrder)
        return
      }

      await this.getParkingInfo(this.$route.query, getParkingInfo)
    },
    formatChargeTime(chargeTime) {
      const duration = Number(chargeTime)
      if (!Number.isFinite(duration) || duration <= 0) {
        return '-'
      }

      return `${Math.round(duration * 60)}分钟（减免）`
    },
    /**
     * 获取停车信息
     */
    async getParkingInfo(params, requestParkingInfo) {
      try {
        this.loading = true
        const res = await requestParkingInfo(params)
        this.parkingInfo = this._formatData(res)
        // 查询是否缴费成功
        // const statusRes = await getOrderStatus({
        //   orderId: res.id,
        //   tradeType: 0
        // })
        // if (statusRes.orderStatus === 1) {
        //   this.isSuccess = true
        // }
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    /**
     * 格式化数据
     * @param {*} data
     */
    _formatData(data) {
      data.licensePlateNumber =
        data.licensePlateNumber.substr(0, 2) +
        '·' +
        data.licensePlateNumber.substr(2)
      if (data.parkTime) {
        const h = moment.duration(data.parkTime, 'hour')
        let str = ''
        if (h.days() > 0 && h.hours() > 0) {
          str += `${h.days()}天`
        } else if (h.days() > 0 && h.hours() === 0) {
          str += `${h.days()}天${h.hours()}小时`
        }
        if (h.hours() > 0) {
          str += `${h.hours()}小时`
        }
        if (h.minutes() > 0) {
          str += `${h.minutes()}分钟`
        }

        data.parkTime = str
      }

      if (data.parkStartTime) {
        data.parkStartTime = moment(data.parkStartTime).format(
          'YYYY-MM-DD HH:mm:ss'
        )
      }

      return data
    },
    /**
     * 处理缴费
     */
    async handlePay() {
      if (this.paying) {
        return
      }
      const { id: orderId } = this.parkingInfo
      try {
        const payload = buildParkingPayPayload({
          orderId,
          isWechatPayPark: this.isWechatPayPark,
          isAli: this.isAli,
          loginCode: this.loginCode
        })
        this.paying = true
        const res = await parkingPay(payload)
        if (this.isAli) {
          window.location.href = res
          return
        }
        if (res) {
          const payData = normalizeWechatPayData(res)
          await this.onBridgeReady(payData)
        }
      } catch (error) {
        this.$toast(error.message)
      } finally {
        this.paying = false
      }
    },
    onBridgeReady(payData) {
      return new Promise((resolve, reject) => {
        const invoke = () => {
          window.WeixinJSBridge.invoke(
            'getBrandWCPayRequest', {
              appId: payData.appId,
              timeStamp: payData.timeStamp,
              nonceStr: payData.nonceStr,
              package: payData.package,
              signType: payData.signType,
              paySign: payData.paySign
            },
            (res) => {
              if (res.err_msg === 'get_brand_wcpay_request:ok') {
                this.isSuccess = true
                this.$toast('支付成功')
              } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
                this.$toast('用户取消支付')
              } else {
                this.$toast('支付失败')
              }
              resolve(res)
            }
          )
        }

        if (typeof window.WeixinJSBridge === 'undefined') {
          document.addEventListener('WeixinJSBridgeReady', invoke, { once: true })
          if (!/MicroMessenger/i.test(window.navigator.userAgent)) {
            reject(new Error('请在微信中完成支付'))
          }
          return
        }
        invoke()
      })
    }
  }
}
</script>

<style lang="scss">
.parking-fee-container {
  height: 100vh;

  .nav-bar {
    height: 44px;
    background-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .leading {
      position: absolute;
      left: 12px;
    }

    .title {
      font-size: 16px;
      font-weight: 700;
      color: #333;
    }
  }

  .main {
    margin: 10px 12px;

    .header {
      background-color: #fff;
      border-radius: 4px;
      padding: 14px 16px 16px;
      box-sizing: border-box;

      .car-no {
        margin-top: 9px;
        background-color: #f6fafd;
        height: 50px;
        border-radius: 4px;
        color: #4d86e0;
        font-weight: bold;
        font-size: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .content {
      margin-top: 10px;
      background-color: #fff;
      padding: 15px 16px 25px;
      border-radius: 4px;

      .title {
        text-align: center;
        font-size: 14px;
        font-weight: 700;
        color: #333;
        border-bottom: 1px solid #f5f5f5;
        padding-bottom: 15px;
      }

      .list {
        margin-top: 26px;

        .item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 19px;

          &:first-child {
            margin-top: 0;
          }

          .leading {
            font-size: 14px;
            font-weight: 500;
            color: #666;
          }

          .trailing {
            font-size: 14px;
            font-weight: 700;
            color: #333;

            &.primary {
              color: #4d86e0;
            }
          }
        }
      }
    }

    .footer {
      margin-top: 30px;

      .pay-btn {
        background-color: #4d86e0;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 16px;
        font-weight: 700;
        border-radius: 4px;

        &.disabled {
          opacity: 0.6;
          pointer-events: none;
        }
      }

      .explain {
        margin-top: 30px;

        .title {
          font-size: 14px;
          font-weight: 700;
          color: #333;
          text-align: center;
        }

        .desc-list {
          margin-top: 20px;
          color: #666;
          font-weight: 500;
          font-size: 12px;
          line-height: 2;

          .strong {
            font-size: 13px;
            color: #333;
            margin-left: 5px;
          }
        }
      }
    }
  }

  .other-status {
    margin-top: 180px;
    text-align: center;

    .icon {
      width: 60px;
      height: 60px;
    }

    .status {
      margin-top: 22px;
      font-size: 16px;
      font-weight: bold;
      color: #333;
    }

    .hint {
      margin-top: 16px;
      font-size: 14px;
      font-weight: 500;
      color: #666;
    }

    .no-data {
      width: 200px;
      height: 200px;
    }

    .no-vehicle {
      font-size: 16px;
      font-weight: 500;
      color: #999;
      margin-top: -10px;
    }
  }
}
</style>
